import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ToastContainer, toast } from 'react-toastify';
import VenueAgreementsEditor from "../../shared/components/VenueAgreementsEditor";
import SeatingChartCard from '../../ConfirmTicketing/components/SeatingChartCard';
import CreateSeatingChartDesigner from '../../ConfirmTicketing/components/CreateSeatingChartDesigner';
import SponsorshipSettings from '../../../shared/SponsorshipSettings';
import { TextInput, Toggle } from '@opendate-app/setlist';
import { maxTicketsError } from '../../../shared/maxTicketsError';
import { updateTeamSetting } from '../../../shared/api/team';

import CKEditorContainer from '../../CKEditor/CKEditorContainer';

import MaskedInput from 'react-text-mask'

const axios = require("axios").default;
import { WEBSITE_RATIO_OPTIONS } from '../../../shared/websiteImageRatios';
import OrderFormFields from './OrderFormFields';

const SECTION_DIVIDER_BORDER = "1px solid var(--sl-border-secondary, #E9EAEB)";

const DeliveryType = ({
  csrfToken,
  team,
  name,
  description,
  value,
  selectedVenueOwnership,
  selectedVenueOwnershipChanged,
  onImmediateSave,
}) => {
  return (
    <div
      className="mb-2 rounded p-3 d-flex align-items-top"
      style={{
        cursor: "pointer",
        borderWidth: "2px",
        borderStyle: "solid",
        borderColor: (value === selectedVenueOwnership.delivery_type ? "var(--sl-fg-brand, #1982C4)" : "var(--sl-border-primary, #D5D7DA)"),
      }}
      onClick={() => {
        const updated = {
          ...selectedVenueOwnership,
          delivery_type: value,
          delivery_time: null
        };

        selectedVenueOwnershipChanged(updated);
        if (onImmediateSave) onImmediateSave(updated);
      }}
    >
      <div className="pr-3">
        <input
          type="radio"
          style={{cursor: "pointer"}}
          checked={value === selectedVenueOwnership.delivery_type}
          onChange={() => {}}
        />
      </div>
      <div>
        <p className="mb-0">
          <strong>
            {name}
          </strong>
        </p>
        <p className="mb-0">{description}</p>
      </div>
    </div>
  );
}

const DefaultSettingToggle = ({
  id,
  label,
  field,
  selectedVenueOwnership,
  selectedVenueOwnershipChanged,
  onImmediateSave,
  className,
}) => {
  return (
    <div className={`custom-control custom-switch${className ? ` ${className}` : ""}`}>
      <input
        id={id}
        type="checkbox"
        className="custom-control-input"
        checked={selectedVenueOwnership[field] ?? false}
        onChange={e => {
          const updated = { ...selectedVenueOwnership, [field]: e.target.checked };
          selectedVenueOwnershipChanged(updated);
          if (onImmediateSave) onImmediateSave(updated);
        }}
      />
      <label className="custom-control-label" style={{"fontSize": "14px"}} htmlFor={id}>
        {label}
      </label>
    </div>
  );
}

const DeliveryTab = ({
  csrfToken,
  team,
  selectedVenueOwnership,
  selectedVenueOwnershipChanged,
  onImmediateSave,
  onDebouncedSave,
}) => {
  return (
    <div style={{fontSize: "14px"}}>
      <div className="row">
        <div className="col-12">
          <h3 style={{fontSize: "18px"}}>
            <strong>
              Default Delivery Method
            </strong>
          </h3>
        </div>
      </div>
      <div className="mt-2 row">
        <div className="col-12">
          <p>Specify your default ticket delivery method. You can change this on an event-by-event basis.</p>
        </div>
      </div>
      <div className="mt-2 row">
        <div className="col-12 col-xl-6">
          <DeliveryType
            csrfToken={csrfToken}
            team={team}
            name="Immediate PDF Delivery"
            description="Upon purchase the fan will receive tickets as a PDF."
            value="immediate_email"
            selectedVenueOwnership={selectedVenueOwnership}
            selectedVenueOwnershipChanged={selectedVenueOwnershipChanged}
            onImmediateSave={onImmediateSave}
          />
          <DeliveryType
            csrfToken={csrfToken}
            team={team}
            name="Immediate Electronic Delivery"
            description="Upon purchase the fan will receive a link to add tickets to their digital wallet."
            value="immediate_electronic"
            selectedVenueOwnership={selectedVenueOwnership}
            selectedVenueOwnershipChanged={selectedVenueOwnershipChanged}
            onImmediateSave={onImmediateSave}
          />
          <DeliveryType
            csrfToken={csrfToken}
            team={team}
            name="Delayed PDF Delivery"
            description="At a date you determine the fan will receive an email with tickets attached as a PDF."
            value="delayed_email"
            selectedVenueOwnership={selectedVenueOwnership}
            selectedVenueOwnershipChanged={selectedVenueOwnershipChanged}
            onImmediateSave={onImmediateSave}
          />
          <DeliveryType
            csrfToken={csrfToken}
            team={team}
            name="Delayed Electronic Delivery"
            description="At a date you determine the fan will receive a link to add tickets to their digital wallet."
            value="delayed_electronic"
            selectedVenueOwnership={selectedVenueOwnership}
            selectedVenueOwnershipChanged={selectedVenueOwnershipChanged}
            onImmediateSave={onImmediateSave}
          />
        </div>
        <div className="pl-xl-0 col-12 col-xl-6">
          {["delayed_email", "delayed_electronic"].includes(selectedVenueOwnership.delivery_type) ? (
            <div className="border rounded p-3">
              <p>
                <strong>
                  {selectedVenueOwnership.delivery_type === "delayed_email"
                    ? "Delayed Email Delivery"
                    : "Delayed Electronic Delivery"}
                </strong>
              </p>
              <p>Specify the date on which you would like tickets to be delivered.</p>
              <p>The delivery date will be calculated for each event based on the following. You can update a specific event delivery date in the ticketing setup flow.</p>
              <div className="d-flex flex-row flex-xl-column align-items-right">
                <div className="mb-xl-2 d-flex align-items-center">
                  <i className="far fa-paper-plane mr-2" />
                  <p className="m-0 mr-2">Ticket Delivery</p>
                </div>
                <div className="d-flex align-items-center">
                  <div
                    className="px-3 py-2 rounded d-flex align-items-center"
                    style={{backgroundColor: "var(--sl-bg-secondary, #FAFAFA)"}}
                  >
                    <span>@</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className="ml-2 form-control form-control-sm"
                      style={{width: "50px", textAlign: "right"}}
                      value={selectedVenueOwnership.delivery_time_interval ?? 1}
                      onChange={e => {
                        const updated = {
                          ...selectedVenueOwnership,
                          delivery_time_interval: parseInt(e.target.value) || 1
                        };
                        selectedVenueOwnershipChanged(updated);
                        if (onDebouncedSave) onDebouncedSave(updated);
                      }}
                    />
                    <select
                      className="form-control form-control-sm ml-2"
                      style={{width: "80px"}}
                      value={selectedVenueOwnership.delivery_time_unit || "hours"}
                      onChange={(e) => {
                        const updated = {
                          ...selectedVenueOwnership,
                          delivery_time_unit: e.target.value
                        };
                        selectedVenueOwnershipChanged(updated);
                        if (onImmediateSave) onImmediateSave(updated);
                      }}
                    >
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                      <option value="weeks">weeks</option>
                      <option value="months">months</option>
                    </select>
                    <span className="ml-2">Before</span>
                  </div>
                  <i className="far fa-calendar mx-2" />
                  <p className="m-0">Event Start</p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const TicketingSettings = ({
  csrfToken,
  team,
  currentUser,
  venueOwnerships,
  selectedVenueOwnership,
  selectedVenueOwnershipChanged,
  updateVenueOwnership,
  maxDescriptorLength,
  seatingSettings,
  seatingSettingsUpdated,
  customFieldsChanged,
  eventOrderFormAvailable,
}) => {
  const [showCreateDesigner, setShowCreateDesigner] = useState(false);
  const [maxTicketsInput, setMaxTicketsInput] = useState(null);
  useEffect(() => { setMaxTicketsInput(null); }, [selectedVenueOwnership.id]);
  const [createContext, setCreateContext] = useState('admission');

  const [confirmationInfo, setConfirmationInfo] = useState(selectedVenueOwnership.ticket_confirmation_info || '');
  const [collectWaitlistInterest, setCollectWaitlistInterest] = useState(team.collect_waitlist_interest_enabled !== false);
  const [notifyWaitlistOnAvailability, setNotifyWaitlistOnAvailability] = useState(team.notify_waitlist_on_availability_enabled !== false);
  const [guestListManifested, setGuestListManifested] = useState(team.guest_list_manifested === true);

  useEffect(() => {
    setConfirmationInfo(selectedVenueOwnership.ticket_confirmation_info || '');
  }, [selectedVenueOwnership.id]);

  const [confirmationPageTemplates, setConfirmationPageTemplates] = useState([]);
  const [editingConfirmationPageTemplate, setEditingConfirmationPageTemplate] = useState(null);

  useEffect(() => {
    let ignore = false;
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.get("/teams/" + team.id + "/confirmation_page_templates")
      .then(({ data }) => { if (!ignore) setConfirmationPageTemplates(data); })
      .catch((err) => { if (!ignore) showToastError(err); });
    return () => { ignore = true; };
  }, []);

  const debounceRef = useRef(null);

  const saveVO = useCallback((vo) => {
    updateVenueOwnership(csrfToken, team, vo);
  }, [csrfToken, team, updateVenueOwnership]);

  const debouncedSave = useCallback((vo) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => saveVO(vo), 500);
  }, [saveVO]);

  const immediateSave = useCallback((vo) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    saveVO(vo);
  }, [saveVO]);

  const handleDuplicateComplete = (copiedChart) => {
    const currentSeatingSettings = seatingSettings[selectedVenueOwnership.id];
    let updatedSeatingSettings = { ...currentSeatingSettings };

    if (copiedChart.admission_chart) {
      const currentCharts = updatedSeatingSettings.admission_seating_charts || [];
      updatedSeatingSettings = {
        ...updatedSeatingSettings,
        admission_seating_charts: [...currentCharts, copiedChart]
      };
    }

    if (copiedChart.add_on_chart) {
      const currentCharts = updatedSeatingSettings.add_on_seating_charts || [];
      updatedSeatingSettings = {
        ...updatedSeatingSettings,
        add_on_seating_charts: [...currentCharts, copiedChart]
      };
    }

    seatingSettingsUpdated(selectedVenueOwnership.id, updatedSeatingSettings);
  };

  const handleChartSynced = (syncedChart) => {
    const currentSeatingSettings = seatingSettings[selectedVenueOwnership.id] || {};
    let updatedSeatingSettings = { ...currentSeatingSettings };

    if (syncedChart.admission_chart) {
      const charts = (currentSeatingSettings.admission_seating_charts || []).map(chart => chart.id === syncedChart.id ? syncedChart : chart);
      updatedSeatingSettings = { ...updatedSeatingSettings, admission_seating_charts: charts };
    }

    if (syncedChart.add_on_chart) {
      const charts = (currentSeatingSettings.add_on_seating_charts || []).map(chart => chart.id === syncedChart.id ? syncedChart : chart);
      updatedSeatingSettings = { ...updatedSeatingSettings, add_on_seating_charts: charts };
    }

    seatingSettingsUpdated(selectedVenueOwnership.id, updatedSeatingSettings);
  };

  const handleChartCreated = (newChart) => {
    
    const currentSeatingSettings = seatingSettings[selectedVenueOwnership.id] || {};
    let updatedSeatingSettings = { ...currentSeatingSettings };

    if (newChart.admission_chart) {
      const charts = [ ...(currentSeatingSettings.admission_seating_charts || []), newChart ];
      updatedSeatingSettings = { ...updatedSeatingSettings, admission_seating_charts: charts };
    }

    if (newChart.add_on_chart) {
      const charts = [ ...(currentSeatingSettings.add_on_seating_charts || []), newChart ];
      updatedSeatingSettings = { ...updatedSeatingSettings, add_on_seating_charts: charts };
    }

    seatingSettingsUpdated(selectedVenueOwnership.id, updatedSeatingSettings);
  };

  const handleChartDestroyed = (destroyedChart) => {
    const currentSeatingSettings = seatingSettings[selectedVenueOwnership.id] || {};
    let updatedSeatingSettings = { ...currentSeatingSettings };

    if (destroyedChart.admission_chart) {
      updatedSeatingSettings.admission_seating_charts = updatedSeatingSettings.admission_seating_charts.filter(chart => chart.id !== destroyedChart.id);
    }

    if (destroyedChart.add_on_chart) {
      updatedSeatingSettings.add_on_seating_charts = updatedSeatingSettings.add_on_seating_charts.filter(chart => chart.id !== destroyedChart.id);
    }

    seatingSettingsUpdated(selectedVenueOwnership.id, updatedSeatingSettings);
  };

  const showToastError = (err) => {
    toast.error(err.response.data.join(", "), {
      position: toast.POSITION.TOP_CENTER,
      draggable: false,
      closeOnClick: false,
      autoClose: 5000,
      hideProgressBar: true
    });
  };

  const currentVenueOwnershipSeatingSettings = seatingSettings[selectedVenueOwnership.id];
  const venueAdmissionSeatingCharts = currentVenueOwnershipSeatingSettings.admission_seating_charts || [];
  const venueAddOnSeatingCharts = currentVenueOwnershipSeatingSettings.add_on_seating_charts || [];

  const saveConfirmationPageTemplate = () => {
    const template = editingConfirmationPageTemplate;
    if (!template || !template.name || template.name.trim().length === 0) return;

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

    const payload = {
      confirmation_page_template: {
        name: template.name,
        body: template.body || "",
        enabled_by_default: template.enabled_by_default || false
      }
    };

    const request = template.id
      ? axios.put("/teams/" + team.id + "/confirmation_page_templates/" + template.id, payload)
      : axios.post("/teams/" + team.id + "/confirmation_page_templates", payload);

    request
      .then(({ data }) => {
        setConfirmationPageTemplates((current) => {
          const exists = current.some((t) => t.id === data.id);
          const next = exists ? current.map((t) => (t.id === data.id ? data : t)) : [...current, data];
          // The backend enforces one default per team, so mirror that: if the
          // saved template is now the default, clear the badge on the others.
          return data.enabled_by_default
            ? next.map((t) => (t.id === data.id ? t : { ...t, enabled_by_default: false }))
            : next;
        });
        setEditingConfirmationPageTemplate(null);
        toast.success("Template saved", {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      })
      .catch((err) => {
        showToastError(err);
      });
  };

  const deleteConfirmationPageTemplate = (template) => {
    if (!window.confirm("Are you sure?")) return;

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.delete("/teams/" + team.id + "/confirmation_page_templates/" + template.id)
      .then(() => {
        setConfirmationPageTemplates((current) => current.filter((t) => t.id !== template.id));
        if (editingConfirmationPageTemplate && editingConfirmationPageTemplate.id === template.id) {
          setEditingConfirmationPageTemplate(null);
        }
      })
      .catch((err) => {
        showToastError(err);
      });
  };


  return(<div>
    <ToastContainer icon={false} />
    <div className="row">
      <div className="col-12">
        <div>
          <div>
            <select
              className="custom-select"
              style={{ width: 280 }}
              onChange={
                (e) => {
                  if (debounceRef.current) clearTimeout(debounceRef.current);
                  var venueOwnershipId = parseInt(e.target.value);
                  var venueOwnership = venueOwnerships.find((vo) =>
                    vo.id === venueOwnershipId
                  );

                  selectedVenueOwnershipChanged(venueOwnership);
                }
              }
              value={selectedVenueOwnership.id || ""}
            >
              {[...venueOwnerships].sort((a, b) =>
                a.venueable.name.localeCompare(b.venueable.name)
              ).map((venueOwnership) =>
                <option key={venueOwnership.id}
                        value={venueOwnership.id}>
                  {venueOwnership.venueable.name}
                </option>
              )}
            </select>
          </div>
          <div className="row" style={{"marginTop": "27px"}}>
            <div className="col-12">
              <p className="mb-1 text-dark"
                 style={{"fontSize": "16px"}}>
                <strong>Credit Card Statement Descriptor</strong>
              </p>
              <div className="row">
                <div className="col-12 col-md-8">
                  <p className="m-0 small text-muted">
                     Statement descriptors explain charges or payments on bank statements. Determine how your ticketed event transactions appear on your customer’s statements.
                  </p>
                </div>
              </div>
              <div className="form-row" style={{"marginTop": "25px"}}>
                <div className="col col-md-6 col-lg-4">
                  <div className="input-group input-group-sm"
                       style={{
                         "fontSize": "14px"
                       }}>
                    <div className="input-group-prepend">
                      <span className="input-group-text border-0"
                            style={{
                              "color": "var(--sl-fg-brand, #1982C4)",
                              "fontFamily": "Courier New",
                              "background": "var(--sl-bg-active, #f4f9fc)"
                            }}>
                        <strong>OD*&nbsp;</strong>
                      </span>
                    </div>
                    <MaskedInput
                      mask={
                        () => {
                          return Array.from(Array(maxDescriptorLength).keys())
                            .map((i) => /[^<>\\'"*]+/);
                        }
                      }
                      className="form-control"
                      style={{"border": "1px solid var(--sl-border-primary, #D5D7DA)"}}
                      guide={false}
                      onBlur={() => {}}
                      value={selectedVenueOwnership.statement_descriptor_suffix || ""}
                      onChange={
                        (e) => {
                          var updated = Object.assign({}, selectedVenueOwnership, {statement_descriptor_suffix: e.target.value});
                          selectedVenueOwnershipChanged(updated);
                          debouncedSave(updated);
                        }
                      }
                    />
                  </div>
                </div>
                <div className="col-xs-auto d-flex align-items-center"
                     style={{"paddingRight": "15px"}}>
                  <p className="mb-0 text-muted" style={{"fontSize": "10px"}}>
                    {(maxDescriptorLength - selectedVenueOwnership.statement_descriptor_suffix.length) + " characters remaining"}
                  </p>
                </div>
              </div>
              <hr style={{
                    "borderTop": SECTION_DIVIDER_BORDER,
                    "marginTop": "56px",
                    "marginBottom": "32px"
                  }} />
            </div>
          </div>
          <SponsorshipSettings
            entity={selectedVenueOwnership}
            onEntityChange={selectedVenueOwnershipChanged}
            onImmediateSave={immediateSave}
            onDebouncedSave={debouncedSave}
          />
          <hr
            style={{
              "borderTop": SECTION_DIVIDER_BORDER,
              "marginTop": "32px",
              "marginBottom": "32px"
            }}
          />
          <div className="row">
            <div className="col-12">
              <p className="mb-1 text-dark"
                 style={{"fontSize": "16px"}}>
                <strong>Ticket Pricing Display</strong>
              </p>
              <div className="row">
                <div className="col-12 col-md-8">
                  <p className="m-0 small text-muted">
                    Control how ticket prices are displayed on the checkout page when all-in pricing is enabled.
                  </p>
                </div>
              </div>
              <div className="row" style={{"marginTop": "25px"}}>
                <div className="col-12">
                  <div className="custom-control custom-switch">
                    <input
                      id="show-fee-breakdown-on-ticket-price"
                      type="checkbox"
                      className="custom-control-input"
                      checked={selectedVenueOwnership.show_fee_breakdown_on_ticket_price !== false}
                      onChange={e => {
                        var updated = Object.assign({}, selectedVenueOwnership, {
                          show_fee_breakdown_on_ticket_price: e.target.checked
                        });
                        selectedVenueOwnershipChanged(updated);
                        immediateSave(updated);
                      }}
                    />
                    <label
                      className="custom-control-label"
                      style={{"fontSize": "14px"}}
                      htmlFor="show-fee-breakdown-on-ticket-price"
                    >
                      Show fee breakdown under ticket prices (e.g., "($15.00 + $3.50 Fees)")
                    </label>
                  </div>
                  <div className="custom-control custom-switch mt-3">
                    <input
                      id="include-taxes-in-all-in-pricing"
                      type="checkbox"
                      className="custom-control-input"
                      checked={selectedVenueOwnership.include_taxes_in_all_in_pricing === true}
                      onChange={e => {
                        var updated = Object.assign({}, selectedVenueOwnership, {
                          include_taxes_in_all_in_pricing: e.target.checked
                        });
                        selectedVenueOwnershipChanged(updated);
                        immediateSave(updated);
                      }}
                    />
                    <label
                      className="custom-control-label"
                      style={{"fontSize": "14px"}}
                      htmlFor="include-taxes-in-all-in-pricing"
                    >
                      Include taxes in ticket price breakdown (e.g., "($15.00 + $4.50 Fees & Taxes)")
                    </label>
                  </div>
                </div>
              </div>
              <hr style={{
                    "borderTop": SECTION_DIVIDER_BORDER,
                    "marginTop": "32px",
                    "marginBottom": "32px"
                  }} />
              <p className="mb-1 text-dark" style={{"fontSize": "16px"}}>
                <strong>New Ticket Type Defaults</strong>
              </p>
              <div className="row">
                <div className="col-12 col-md-8">
                  <p className="m-0 small text-muted">
                    Pre-select these options when creating a new ticket type. They can still be changed per ticket.
                  </p>
                </div>
              </div>
              <div className="row" style={{"marginTop": "25px"}}>
                <div className="col-12">
                  <DefaultSettingToggle
                    id="default-round-up-ticket-types"
                    label="Round up pricing on by default"
                    field="default_round_up_ticket_types"
                    selectedVenueOwnership={selectedVenueOwnership}
                    selectedVenueOwnershipChanged={selectedVenueOwnershipChanged}
                    onImmediateSave={immediateSave}
                  />
                  {team.package && team.package.dynamic_pricing && (
                    <DefaultSettingToggle
                      id="default-dynamic-pricing-ticket-types"
                      label="Dynamic pricing on by default"
                      field="default_dynamic_pricing_ticket_types"
                      selectedVenueOwnership={selectedVenueOwnership}
                      selectedVenueOwnershipChanged={selectedVenueOwnershipChanged}
                      onImmediateSave={immediateSave}
                      className="mt-3"
                    />
                  )}
                </div>
              </div>
              <p className="mb-1 mt-4 text-dark" style={{"fontSize": "16px"}}>
                <strong>New Add-On Defaults</strong>
              </p>
              <div className="row">
                <div className="col-12 col-md-8">
                  <p className="m-0 small text-muted">
                    Pre-select these options when creating a new add-on. They can still be changed per add-on.
                  </p>
                </div>
              </div>
              <div className="row" style={{"marginTop": "25px"}}>
                <div className="col-12">
                  <DefaultSettingToggle
                    id="default-round-up-add-ons"
                    label="Round up pricing on by default"
                    field="default_round_up_add_ons"
                    selectedVenueOwnership={selectedVenueOwnership}
                    selectedVenueOwnershipChanged={selectedVenueOwnershipChanged}
                    onImmediateSave={immediateSave}
                  />
                  {team.package && team.package.dynamic_pricing && (
                    <DefaultSettingToggle
                      id="default-dynamic-pricing-add-ons"
                      label="Dynamic pricing on by default"
                      field="default_dynamic_pricing_add_ons"
                      selectedVenueOwnership={selectedVenueOwnership}
                      selectedVenueOwnershipChanged={selectedVenueOwnershipChanged}
                      onImmediateSave={immediateSave}
                      className="mt-3"
                    />
                  )}
                </div>
              </div>
              <hr style={{
                    "borderTop": SECTION_DIVIDER_BORDER,
                    "marginTop": "32px",
                    "marginBottom": "32px"
                  }} />
            </div>
          </div>
          {(team.can_enable_checkout_captcha || selectedVenueOwnership.checkout_captcha_default_on) && (
            <div className="row">
              <div className="col-12">
                <p className="mb-1 text-dark" style={{"fontSize": "16px"}}>
                  <strong>Checkout CAPTCHA</strong>
                </p>
                <div className="row">
                  <div className="col-12 col-md-8">
                    {!team.can_enable_checkout_captcha && selectedVenueOwnership.checkout_captcha_default_on ? (
                      <p className="m-0 small text-muted">
                        Checkout CAPTCHA is on by default for this venue's new events, but your team no longer has access to this feature. You can turn the default off below.
                      </p>
                    ) : (
                      <p className="m-0 small text-muted">
                        CAPTCHA verification protects your events from fraud and abuse. When enabled, every new event starts with checkout CAPTCHA turned on. You can still turn it off for an individual event. Verification is powered by Cloudflare Turnstile.
                      </p>
                    )}
                  </div>
                </div>
                <div className="row" style={{"marginTop": "25px"}}>
                  <div className="col-12">
                    <DefaultSettingToggle
                      id="checkout-captcha-default-on"
                      label="Enable checkout CAPTCHA on all new events"
                      field="checkout_captcha_default_on"
                      selectedVenueOwnership={selectedVenueOwnership}
                      selectedVenueOwnershipChanged={selectedVenueOwnershipChanged}
                      onImmediateSave={immediateSave}
                    />
                  </div>
                </div>
                <hr style={{
                      "borderTop": SECTION_DIVIDER_BORDER,
                      "marginTop": "32px",
                      "marginBottom": "32px"
                    }} />
              </div>
            </div>
          )}
          <div className="row">
            <div className="col-12">
              <p className="mb-1 text-dark" style={{"fontSize": "16px"}}>
                <strong>Sold-Out Waitlist</strong>
              </p>
              <div className="row">
                <div className="col-12 col-md-8">
                  <p className="m-0 small text-muted">
                    When an event sells out, show a waitlist signup form on its checkout page. This applies to every venue on your team. You can still turn it on or off for an individual event.
                  </p>
                </div>
              </div>
              <div className="row" style={{"marginTop": "25px"}}>
                <div className="col-12">
                  <Toggle
                    id="collect-waitlist-interest-enabled"
                    label="Collect a waitlist on sold-out events by default"
                    checked={collectWaitlistInterest}
                    onChange={e => {
                      const checked = e.target.checked;
                      setCollectWaitlistInterest(checked);
                      updateTeamSetting(team.id, { collect_waitlist_interest_enabled: checked });
                    }}
                  />
                </div>
              </div>
              <div className="row" style={{"marginTop": "15px"}}>
                <div className="col-12">
                  <Toggle
                    id="notify-waitlist-on-availability-enabled"
                    label="Email the waitlist when tickets come back"
                    checked={notifyWaitlistOnAvailability}
                    onChange={e => {
                      const checked = e.target.checked;
                      setNotifyWaitlistOnAvailability(checked);
                      updateTeamSetting(team.id, { notify_waitlist_on_availability_enabled: checked });
                    }}
                  />
                </div>
              </div>
              <hr style={{
                    "borderTop": SECTION_DIVIDER_BORDER,
                    "marginTop": "32px",
                    "marginBottom": "32px"
                  }} />
              <p className="mb-1 text-dark" style={{"fontSize": "16px"}}>
                <strong>Guest List Capacity</strong>
              </p>
              <div className="row">
                <div className="col-12 col-md-8">
                  <p className="m-0 small text-muted">
                    Choose whether guest list tickets use up an event's capacity. When off, guests are added on top of capacity and every ticket stays available to sell. This applies to every venue on your team. You can still change it for an individual event from its guest list.
                  </p>
                </div>
              </div>
              <div className="row" style={{"marginTop": "25px"}}>
                <div className="col-12">
                  <Toggle
                    id="guest-list-manifested"
                    label="Count guest list tickets toward capacity by default"
                    checked={guestListManifested}
                    onChange={e => {
                      const checked = e.target.checked;
                      setGuestListManifested(checked);
                      updateTeamSetting(team.id, { guest_list_manifested: checked }).then((saved) => {
                        if (!saved) setGuestListManifested(!checked);
                      });
                    }}
                  />
                </div>
              </div>
              <hr style={{
                    "borderTop": SECTION_DIVIDER_BORDER,
                    "marginTop": "32px",
                    "marginBottom": "32px"
                  }} />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <p className="mb-1 text-dark"
                 style={{"fontSize": "16px"}}>
                <strong>Website Event Image Resolution</strong>
              </p>
              <div className="row">
                <div className="col-12 col-md-8">
                  <p className="m-0 small text-muted">
                    Determine the default image resolution for event images as they appear on your website.
                  </p>
                </div>
              </div>
              <div className="form-row" style={{"marginTop": "25px"}}>
                <div className="col col-md-6 col-lg-4">
                  <select
                    className="custom-select custom-select-sm"
                    value={selectedVenueOwnership.website_image_ratio || "LANDSCAPE_2_1"}
                    onChange={(e) => {
                      const value = e.target.value;
                      const updated = Object.assign({}, selectedVenueOwnership, { website_image_ratio: value });
                      selectedVenueOwnershipChanged(updated);
                      immediateSave(updated);
                    }}
                  >
                    {WEBSITE_RATIO_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="row" style={{"marginTop": "25px"}}>
                <div className="col-12 col-md-8">
                  <p className="m-0 small text-muted">
                    When enabled, your marketing emails use the Website Event Image you upload at
                    this aspect ratio. Events without one keep using the main event image.
                  </p>
                </div>
              </div>
              <div className="row" style={{"marginTop": "12px"}}>
                <div className="col-12">
                  <DefaultSettingToggle
                    id="ratio-cropped-emails-enabled"
                    label="Use this aspect ratio for event images in emails"
                    field="ratio_cropped_emails_enabled"
                    selectedVenueOwnership={selectedVenueOwnership}
                    selectedVenueOwnershipChanged={selectedVenueOwnershipChanged}
                    onImmediateSave={immediateSave}
                  />
                </div>
              </div>
              <hr style={{
                    "borderTop": SECTION_DIVIDER_BORDER,
                    "marginTop": "32px",
                    "marginBottom": "32px"
                  }} />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <p className="text-dark"
                 style={{
                   "fontSize": "16px",
                   "marginBottom": "12px"
                 }}>
                <strong>Default Ticket Type</strong>
              </p>
              <div className="row">
                <div className="col-12">
                  <div className="custom-control custom-radio">
                    <input type="radio"
                           id="default-ticketing-arrangement-seated"
                           name="default-ticketing-arrangement"
                           value="Seated"
                           checked={selectedVenueOwnership.default_ticketing_arrangement === "Seated"}
                           onChange={
                             (e) => {
                               var updated = Object.assign({}, selectedVenueOwnership, {default_ticketing_arrangement: e.target.value});
                               selectedVenueOwnershipChanged(updated);
                               immediateSave(updated);
                             }
                           }
                           className="custom-control-input" />
                    <label className="custom-control-label"
                           style={{"fontSize": "14px"}}
                           htmlFor="default-ticketing-arrangement-seated">
                      Seated
                    </label>
                  </div>
                  <div className="custom-control custom-radio">
                    <input type="radio"
                           id="default-ticketing-arrangement-general-admission"
                           name="default-ticketing-arrangement"
                           value="General Admission"
                           checked={selectedVenueOwnership.default_ticketing_arrangement === "General Admission"}
                           onChange={
                             (e) => {
                               var updated = Object.assign({}, selectedVenueOwnership, {default_ticketing_arrangement: e.target.value});
                               selectedVenueOwnershipChanged(updated);
                               immediateSave(updated);
                             }
                           }
                           className="custom-control-input" />
                    <label className="custom-control-label"
                           style={{"fontSize": "14px"}}
                           htmlFor="default-ticketing-arrangement-general-admission">
                      General Admission
                    </label>
                  </div>
                </div>
              </div>
              <hr style={{
                    "borderTop": SECTION_DIVIDER_BORDER,
                    "marginTop": "25px",
                    "marginBottom": "32px"
                  }} />
            </div>
          </div>
          {currentVenueOwnershipSeatingSettings.seating_designer_available ? (
            <div className="row">
              <div className="col-12">
                {currentVenueOwnershipSeatingSettings.seating_designer_available && (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <p className="text-dark mb-0"
                         style={{
                           "fontSize": "16px"
                         }}>
                        <strong>Seating Charts</strong>
                      </p>
                      <button
                        type="button"
                        className="team-btn-secondary team-btn-sm"
                        onClick={() => { setCreateContext('admission'); setShowCreateDesigner(true); }}
                      >
                        + Create new
                      </button>
                    </div>
                    {venueAdmissionSeatingCharts.length > 0 && (
                      <div className="row">
                      {venueAdmissionSeatingCharts.map(chart => (
                        <SeatingChartCard
                          key={chart.id}
                          seatingChart={chart}
                          csrfToken={csrfToken}
                          isAddOn={false}
                          showSettings={true}
                          onDuplicateComplete={handleDuplicateComplete}
                          onChartSynced={handleChartSynced}
                          onChartDestroyed={handleChartDestroyed}
                        />
                      ))}
                      </div>
                    )}
                    <hr style={{
                      "borderTop": SECTION_DIVIDER_BORDER,
                      "marginTop": "25px",
                      "marginBottom": "32px"
                    }} />
                  </>
                )}
                {currentVenueOwnershipSeatingSettings.seating_designer_available && (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <p className="text-dark mb-0"
                         style={{
                           "fontSize": "16px"
                         }}>
                        <strong>Add-on Seating Charts</strong>
                      </p>
                      <button
                        type="button"
                        className="team-btn-secondary team-btn-sm"
                        onClick={() => { setCreateContext('add_on'); setShowCreateDesigner(true); }}
                      >
                        + Create new
                      </button>
                    </div>
                    {venueAddOnSeatingCharts.length > 0 && (
                      <div className="row">
                      {venueAddOnSeatingCharts.map(chart => (
                        <SeatingChartCard
                          key={chart.id}
                          seatingChart={chart}
                          csrfToken={csrfToken}
                          isAddOn={true}
                          showSettings={true}
                          onDuplicateComplete={handleDuplicateComplete}
                          onChartSynced={handleChartSynced}
                          onChartDestroyed={handleChartDestroyed}
                        />
                      ))}
                      </div>
                    )}
                    <hr style={{
                      "borderTop": SECTION_DIVIDER_BORDER,
                      "marginTop": "25px",
                      "marginBottom": "32px"
                    }} />
                  </>
                )}
              </div>
            </div>
          ) : null}

          {showCreateDesigner && (
            <CreateSeatingChartDesigner
              show={showCreateDesigner}
              onClose={() => setShowCreateDesigner(false)}
              venueOwnershipId={selectedVenueOwnership.id}
              teamId={team.id}
              context={createContext}
              onChartCreated={handleChartCreated}
            />
          )}

          <div className="row">
            <div className="col-12">
              <p className="text-dark"
                  style={{
                    "fontSize": "16px",
                    "marginBottom": "4px"
                  }}>
                <strong>Maximum Tickets Allowed per Event</strong>
              </p>
              <div className="row">
                <div className="col-12 col-md-8">
                  <p className="m-0 small text-muted"
                    style={{
                      "paddingBottom": "16px"
                    }}
                  >
                    Define the maximum number of tickets that can be purchased by a fan across multiple orders for a single event. If a fan attempts to purchase more than the maximum number of tickets allowed per event, they will receive an alert notifying them that the event has a maximum and the order will not process.
                  </p>
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-md-6">
                  <div style={{ maxWidth: "360px" }}>
                    <TextInput
                      id="venue-ownership-max-tickets-per-email"
                      label="Maximum Tickets Allowed"
                      size="sm"
                      inputMode="numeric"
                      placeholder="No limit"
                      value={maxTicketsInput ?? (selectedVenueOwnership.max_tickets_per_email ?? "")}
                      error={maxTicketsError(maxTicketsInput)}
                      onChange={
                        (e) => {
                          var value = e.target.value.replace(/[^0-9]/g, "").slice(0, 3);
                          setMaxTicketsInput(value);
                          if (maxTicketsError(value)) {
                            return;
                          }

                          var updated = Object.assign({}, selectedVenueOwnership, {
                            max_tickets_per_email: value
                          });

                          selectedVenueOwnershipChanged(updated);
                          debouncedSave(updated);
                        }
                      }
                    />
                  </div>
                </div>
              </div>
              <hr style={{
                    "borderTop": SECTION_DIVIDER_BORDER,
                    "marginTop": "25px",
                    "marginBottom": "32px"
                  }} />
            </div>
          </div>
          <DeliveryTab
            csrfToken={csrfToken}
            team={team}
            selectedVenueOwnership={selectedVenueOwnership}
            selectedVenueOwnershipChanged={selectedVenueOwnershipChanged}
            onImmediateSave={immediateSave}
            onDebouncedSave={debouncedSave}
          />
          <hr
            style={{
              "borderTop": SECTION_DIVIDER_BORDER,
              "marginTop": "25px",
              "marginBottom": "32px"
            }}
          />
          <div className="row">
            <div className="col-12">
              <p className="text-dark"
                  style={{
                    "fontSize": "16px",
                    "marginBottom": "4px"
                  }}>
                <strong>Agreements</strong>
              </p>
              <div className="row col-12">
                <p className="m-0 small text-muted"
                  style={{
                    "paddingBottom": "16px"
                  }}
                >
                  Include custom agreements on your checkout pages. Required agreements must be accepted before purchase; optional ones can be skipped and can tag the contacts who accept.
                </p>
              </div>
              <VenueAgreementsEditor
                csrfToken={csrfToken}
                teamId={team.id}
                venueOwnershipId={selectedVenueOwnership.id}
              />
              <hr style={{
                    "borderTop": SECTION_DIVIDER_BORDER,
                    "marginTop": "25px",
                    "marginBottom": "32px"
                  }} />
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <OrderFormFields
                team={team}
                askPerAvailable={eventOrderFormAvailable}
                venueOwnership={selectedVenueOwnership}
                customFieldsChanged={customFieldsChanged}
                venueName={selectedVenueOwnership.venueable?.name}
                header={(
                  <>
                    <p className="mb-1" style={{"fontSize": "16px"}}>
                      <strong>Order Form</strong>
                    </p>
                    <p className="mb-0 small text-muted" style={{"maxWidth": "640px"}}>
                      Manage the information you collect during checkout. These fields apply to every event at this venue.
                    </p>
                  </>
                )}
              />
              <hr style={{
                    "borderTop": SECTION_DIVIDER_BORDER,
                    "marginTop": "32px",
                    "marginBottom": "32px"
                  }} />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <p className="mb-1"
                 style={{"fontSize": "16px"}}>
                <strong>Confirmation Page and Email</strong>
              </p>
              <div className="row">
                <div className="col-12 col-md-8">
                  <p className="mb-3 small text-muted">
                    After registering for your events customers will receive a confirmation email with their tickets. Add important venue-specific instructions or any other useful information here. Use the editor's Source mode to embed HTML (for example, a booking widget) &mdash; embeds appear on the order confirmation page, while emails only include the text content.
                  </p>
                </div>
              </div>
              <CKEditorContainer
                content={confirmationInfo}
                setContent={
                  (html) => {
                    setConfirmationInfo(html);
                    const updated = {
                      ...selectedVenueOwnership,
                      ticket_confirmation_info: html,
                    };
                    selectedVenueOwnershipChanged(updated);
                    debouncedSave(updated);
                  }
                }
                containerStyle={{ maxWidth: "800px" }}
                allowScriptEmbeds />
            </div>
          </div>
          <hr style={{
                "borderTop": SECTION_DIVIDER_BORDER,
                "marginTop": "32px",
                "marginBottom": "32px"
              }} />
          <div className="row">
            <div className="col-12">
              <p className="mb-1" style={{"fontSize": "16px"}}>
                <strong>Confirmation Page Templates</strong>
              </p>
              <div className="row">
                <div className="col-12 col-md-8">
                  <p className="mb-3 small text-muted">
                    Save reusable confirmation page layouts here, then turn one on for any individual event from its ticketing settings. Use the editor's Source mode to embed HTML.
                  </p>
                </div>
              </div>
              {confirmationPageTemplates.length > 0 && (
                <div className="mb-3" style={{ maxWidth: "800px" }}>
                  {[...confirmationPageTemplates].sort((a, b) => a.name.localeCompare(b.name)).map((template) => (
                    <div
                      key={template.id}
                      className="d-flex align-items-center justify-content-between border rounded p-2 mb-2"
                    >
                      <span style={{ fontSize: "14px" }}>
                        {template.name}
                        {template.enabled_by_default && (
                          <span className="badge badge-primary ml-2">Default</span>
                        )}
                      </span>
                      <div>
                        <button
                          type="button"
                          className="team-btn-secondary team-btn-sm mr-2"
                          onClick={() => setEditingConfirmationPageTemplate({ ...template })}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="team-btn-secondary team-btn-sm"
                          onClick={() => deleteConfirmationPageTemplate(template)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {editingConfirmationPageTemplate ? (
                <div className="border rounded p-3" style={{ maxWidth: "800px" }}>
                  <label className="text-muted small" htmlFor="confirmation-page-template-name">
                    Template name
                  </label>
                  <input
                    id="confirmation-page-template-name"
                    type="text"
                    className="form-control mb-3"
                    style={{ borderWidth: "1px" }}
                    placeholder="e.g. Standard doors & parking info"
                    value={editingConfirmationPageTemplate.name || ""}
                    onChange={(e) => setEditingConfirmationPageTemplate({
                      ...editingConfirmationPageTemplate,
                      name: e.target.value
                    })}
                  />
                  <CKEditorContainer
                    key={editingConfirmationPageTemplate.id || "new"}
                    content={editingConfirmationPageTemplate.body || ""}
                    setContent={(html) => setEditingConfirmationPageTemplate({
                      ...editingConfirmationPageTemplate,
                      body: html
                    })}
                    containerStyle={{ maxWidth: "100%" }}
                    allowScriptEmbeds
                  />
                  <div className="custom-control custom-switch mt-3">
                    <input
                      id="confirmation-page-template-default"
                      type="checkbox"
                      className="custom-control-input"
                      checked={editingConfirmationPageTemplate.enabled_by_default || false}
                      onChange={(e) => setEditingConfirmationPageTemplate({
                        ...editingConfirmationPageTemplate,
                        enabled_by_default: e.target.checked
                      })}
                    />
                    <label className="custom-control-label" style={{ fontSize: "14px" }} htmlFor="confirmation-page-template-default">
                      Enable by default on all events' confirmation screens
                    </label>
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      className="team-btn-primary team-btn-sm mr-2"
                      disabled={!editingConfirmationPageTemplate.name || editingConfirmationPageTemplate.name.trim().length === 0}
                      onClick={saveConfirmationPageTemplate}
                    >
                      Save Template
                    </button>
                    <button
                      type="button"
                      className="team-btn-secondary team-btn-sm"
                      onClick={() => setEditingConfirmationPageTemplate(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="team-btn-secondary"
                  onClick={() => setEditingConfirmationPageTemplate({ name: "", body: "" })}
                >
                  + New Template
                </button>
              )}
            </div>
          </div>
          <div style={{ paddingBottom: 25 }} />
        </div>
      </div>
    </div>
  </div>
)};

TicketingSettings.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  venueOwnerships: PropTypes.array,
  selectedVenueOwnership: PropTypes.object,
  selectedVenueOwnershipChanged: PropTypes.func.isRequired,
  updateVenueOwnership: PropTypes.func.isRequired,
  maxDescriptorLength: PropTypes.number,
  eventOrderFormAvailable: PropTypes.bool,
  seatingSettings: PropTypes.object,
  seatingSettingsUpdated: PropTypes.func.isRequired,
  customFieldsChanged: PropTypes.func.isRequired,
};

export default TicketingSettings;
